import type React from 'react';
import {useState} from 'react';
import {Box, Button, Divider, Link, Stack, Typography} from '@mui/material';
import {Add, AttachFile} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import {downloadAttachment} from '../../hooks/downloadAttachment';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {CategoryRecord, Requirement} from '../../types/categoryRecordTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RequirementDialog} from './RequirementDialog';
import {RowAction} from '../components/RowAction';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

type Props = {databagId: number; record: CategoryRecord; actions: RecordActions};

/** Business requirements tagged by RAQSCI, with a coverage strip that marks criteria without a requirement. */
export const RequirementsSection: React.FC<Props> = ({databagId, record, actions}) => {
    const [editing, setEditing] = useState<Requirement | 'new' | null>(null);
    const label = (code: string) => record.options.criteria.find(c => c.code === code)?.label ?? code;

    return (
        <ProfileSection
            title={`Business requirements (${record.requirements.length})`}
            subtitle="Regulatory · Assurance of supply · Quality · Service · Cost · Innovation"
            help={OVERVIEW_HELP.requirements}
            actions={record.canEdit && (
                <Button size="small" startIcon={<Add/>} onClick={() => setEditing('new')}>Add requirement</Button>
            )}
        >
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{mb: 1.5}} aria-label="Coverage per RAQSCI criterion">
                {record.coverage.map(c => (
                    <Tag key={c.criterion} solid={!c.gap} color={c.gap ? 'warning' : 'primary'}
                         label={c.gap ? `${c.criterion} · ${c.label}: gap` : `${c.criterion} · ${c.label}: ${c.count}`}/>
                ))}
            </Stack>

            {record.requirements.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No requirements yet.{record.canEdit ? ' Add what suppliers in this category must meet.' : ''}
                </Typography>
            ) : (
                <Stack divider={<Divider flexItem/>}>
                    {record.requirements.map(r => (
                        <Stack key={r.id} direction="row" gap={2} alignItems="flex-start" sx={{py: 1.25}}>
                            <Tag label={r.criterion} color="primary" title={label(r.criterion)} sx={{minWidth: 32}}/>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                                {r.statement && (
                                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>
                                        {r.statement}
                                    </Typography>
                                )}
                                {r.attachment && (
                                    <Link component="button" variant="caption" underline="hover"
                                          onClick={() => void downloadAttachment(databagId, r.id, r.attachment!.name)}
                                          sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                                        <AttachFile sx={{fontSize: 14}}/>{r.attachment.name}
                                    </Link>
                                )}
                            </Box>
                            {record.canEdit && (
                                <Stack direction="row" gap={0.5}>
                                    <RowAction kind="edit" label={`requirement ${r.label}`} onClick={() => setEditing(r)}/>
                                    <RowAction kind="delete" label={`requirement ${r.label}`}
                                               onClick={() => void actions.removeRequirement(r).catch(() => undefined)}/>
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}

            {editing && (
                <RequirementDialog
                    criteria={record.options.criteria}
                    attachmentTypes={record.options.attachmentTypes}
                    requirement={editing === 'new' ? undefined : editing}
                    onSubmit={(input, file, remove) => editing === 'new'
                        ? actions.addRequirement(input, file)
                        : actions.updateRequirement(editing, input, file, remove)}
                    onClose={() => setEditing(null)}
                />
            )}
        </ProfileSection>
    );
};
